export type Book = {
  title: string;
  author: string;
  /** Cover, inside-cover and text colors. */
  bg: string;
  inside: string;
  fg: string;
  /** Where the book sits in the pile (px) and its tilt (deg). */
  x: number;
  y: number;
  rot: number;
  /** The simple shape printed on the cover. */
  motif: { w: number; h: number; radius: string; color: string; shadow: string };
  quote: string;
  takeaway: string;
  notesUrl: string;
};

export type Photo = {
  tint: string;
  tape: string;
  rot: number;
  align: 'flex-start' | 'flex-end';
  caption: string;
  src: string;
  width: number;
  height: number;
  /** Optional URL; the caption becomes a link to it. */
  link?: string;
};

export type AlbumPage = { num: number; photos: Photo[] };

export const OWNER_NAME = 'Kat';

export const books: Book[] = [
  {
    title: 'Thinking, Fast and Slow',
    author: 'Daniel Kahneman',
    bg: '#E4EDEB', inside: '#F2F7F6', fg: '#1E4B48',
    x: 0, y: 36, rot: -10,
    motif: { w: 72, h: 72, radius: '50%', color: '#FCEFA8', shadow: 'none' },
    quote: '[a line from it]',
    takeaway: '[one line on what you do differently because of it]',
    notesUrl: '#medium',
  },
  {
    title: 'The Design of Everyday Things',
    author: 'Don Norman',
    bg: '#1E4B48', inside: '#2F7069', fg: '#FFFFFF',
    x: 134, y: 7, rot: 8,
    motif: { w: 132, h: 10, radius: '5px', color: '#F9E4DE', shadow: '0 19px 0 #F9E4DE, 0 38px 0 #F9E4DE' },
    quote: '[a line from it]',
    takeaway: '[one line on what you do differently because of it]',
    notesUrl: '#medium',
  },
  {
    title: 'Nudge',
    author: 'Thaler and Sunstein',
    bg: '#FCEFA8', inside: '#FEF7D4', fg: '#6B5200',
    x: 31, y: 149, rot: 4,
    motif: { w: 76, h: 76, radius: '50% 50% 50% 5px', color: '#E5634D', shadow: 'none' },
    quote: '[a line from it]',
    takeaway: '[one line on what you do differently because of it]',
    notesUrl: '#medium',
  },
  {
    title: 'Predictably Irrational',
    author: 'Dan Ariely',
    bg: '#F9E4DE', inside: '#FCF1EE', fg: '#B03F2B',
    x: 144, y: 161, rot: -6,
    motif: { w: 68, h: 68, radius: '12px', color: '#FFFFFF', shadow: 'none' },
    quote: '[a line from it]',
    takeaway: '[one line on what you do differently because of it]',
    notesUrl: '#medium',
  },
  {
    title: 'Designing for Behavior Change',
    author: 'Stephen Wendel',
    bg: '#D9CCF7', inside: '#EEE8FB', fg: '#2E2257',
    x: 74, y: 84, rot: -2,
    motif: { w: 70, h: 70, radius: '50%', color: '#FFFFFF', shadow: '0 0 0 10px rgba(255, 255, 255, 0.45)' },
    quote: '[a line from it]',
    takeaway: '[one line on what you do differently because of it]',
    notesUrl: '#medium',
  },
  {
    title: 'Psicología en la era digital',
    author: 'Gloria Victoria Chaves Muñoz',
    bg: '#3D2E6B', inside: '#5A4790', fg: '#FFFFFF',
    x: 92, y: 118, rot: 5,
    motif: { w: 76, h: 64, radius: '50% 50% 46% 46%', color: '#F28DB2', shadow: '0 0 0 8px rgba(242, 141, 178, 0.3)' },
    quote: '[a line from it]',
    takeaway: '[one line on what you do differently because of it]',
    notesUrl: '#medium',
  },
];

/** Album photos live in the coffee-table GitHub repo (default branch). */
const ALBUM_BASE = 'https://raw.githubusercontent.com/sukikatu/coffee-table/HEAD/public/album';

/** Oldest first. Write a caption for each one. */
export const albumPhotos: Omit<Photo, 'tint' | 'tape' | 'rot' | 'align'>[] = [
  { src: `${ALBUM_BASE}/photo-01.jpg`, width: 1000, height: 1000, caption: 'Kat in 2017 with the first MacBook I got and a bunch of stickers from WeWork' },
  { src: `${ALBUM_BASE}/photo-02.jpg`, width: 750, height: 1000, caption: 'The client and project that I will always be proud of, after a full day of workshops' },
  { src: `${ALBUM_BASE}/photo-03.jpg`, width: 666, height: 1000, caption: 'Kat in 2026, to contrast the other one: more sober, more calm 😂' },
  { src: `${ALBUM_BASE}/photo-04.jpg`, width: 1000, height: 750, caption: 'First time in Brazil, at a team launch having fun 🥂' },
  { src: `${ALBUM_BASE}/photo-05.jpg`, width: 750, height: 1000, caption: 'My most beautiful design so far 😍' },
  { src: `${ALBUM_BASE}/photo-06.jpg`, width: 1000, height: 750, caption: 'My first talk of 2026, for Magic Patterns!', link: 'https://www.magicpatterns.com/profile/kat' },
  { src: `${ALBUM_BASE}/photo-07.jpg`, width: 750, height: 1000, caption: "Another one because I can't get enough of him" },
  { src: `${ALBUM_BASE}/photo-08.jpg`, width: 1000, height: 750, caption: 'My son knows how to stick his tongue out for pics now :)' },
  { src: `${ALBUM_BASE}/photo-09.jpg`, width: 750, height: 1000, caption: 'Emotional support dog' },
  { src: `${ALBUM_BASE}/photo-10.jpg`, width: 1000, height: 657, caption: 'My son created a pizza' },
  { src: `${ALBUM_BASE}/photo-11.jpg`, width: 772, height: 1000, caption: '[caption]' },
  { src: `${ALBUM_BASE}/photo-12.jpg`, width: 750, height: 1000, caption: '[caption]' },
];

export const photoTints = ['#F9E4DE', '#E4EDEB', '#FCEFA8', '#D9CCF7', '#DCEFD3', '#FFE0C2'];
export const photoTapes = ['#F9E4DE', '#CDF4D3', '#FCEFA8', '#D9CCF7'];
